import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { useSocket } from "./SocketContext";
import { API_URL } from "../apiservices";

const SystemHealthContext = createContext({
    health: {
        calendarConnected: null, // null means loading/unknown
        availability: null,
        serviceCount: null,
        therapistCount: null
    },
    isBookingDisabled: false, // Default to false until we know for sure
    bookingDisabledReasons: [],
    isLoading: true
});

export const useSystemHealth = () => useContext(SystemHealthContext);

const SystemHealthProvider = ({ children }) => {
    const [health, setHealth] = useState({
        calendarConnected: null,
        availability: null,
        serviceCount: null,
        therapistCount: null
    });
    const [isLoading, setIsLoading] = useState(true);
    const socket = useSocket();

    useEffect(() => {
        const fetchHealth = async () => {
            try {
                const token = localStorage.getItem("accessToken");
                if (!token) {
                    console.log("SystemHealthContext: No token found");
                    return;
                }

                console.log("SystemHealthContext: Fetching system health...");
                const res = await axios.get(`${API_URL}/system-health`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                console.log("SystemHealthContext: API Response:", res.data);
                if (res.data.success) {
                    setHealth(res.data.data);
                }
            } catch (error) {
                console.error("Failed to fetch system health:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchHealth();
    }, []);

    useEffect(() => {
        if (!socket) return;

        const handleHealthUpdate = (data) => {
            console.log("System Health Update via Socket:", data);
            setHealth(data);
            setIsLoading(false); // Update received means we are loaded/synced
        };

        socket.on('system_health_update', handleHealthUpdate);

        return () => {
            socket.off('system_health_update', handleHealthUpdate);
        };
    }, [socket]);

    // Derive status
    const bookingDisabledReasons = [];

    // Only check if we are not loading or have sufficient data
    // Only flag errors if data is explicitly retrieved and failing
    if (!isLoading) {
        if (health.calendarConnected === false) bookingDisabledReasons.push("Google Calendar not connected");
        if (health.availability && health.availability === 'none') bookingDisabledReasons.push("Appointment availability is set to none");
        // Use explicit check for 0 to avoid issues with null (though null shouldn't happen here if !isLoading)
        if (health.serviceCount === 0) bookingDisabledReasons.push("No services with options are setup");
        if (health.therapistCount === 0) bookingDisabledReasons.push("No stylists are setup");
    }

    const isBookingDisabled = bookingDisabledReasons.length > 0;

    return (
        <SystemHealthContext.Provider value={{ health, isBookingDisabled, bookingDisabledReasons, isLoading }}>
            {children}
        </SystemHealthContext.Provider>
    );
};

export default SystemHealthProvider;
